import QRCodeStyling from 'qr-code-styling'

import { TQrDesign } from '../qrImageTypes'
import {
	createQrPatternInstance,
	MAX_LOGO_IMAGE_SIZE,
} from './createQrPatternInstance'

const SHRINK_FACTOR = 0.8
const MAX_SHRINK_STEPS = 5
const QUIET_ZONE_PX = 40

const hasLogo = (qrDesign: TQrDesign) =>
	Boolean(qrDesign.image?.selected || qrDesign.image?.uploaded)

const loadSvgImage = async (svgBlob: Blob) => {
	const url = URL.createObjectURL(svgBlob)
	try {
		return await new Promise<HTMLImageElement | null>((resolve) => {
			const image = new Image()
			image.onload = () => resolve(image)
			image.onerror = () => resolve(null)
			image.src = url
		})
	} finally {
		URL.revokeObjectURL(url)
	}
}

const decodeQrPattern = async (
	qrPattern: QRCodeStyling,
	backgroundColor?: string,
) => {
	const svgBlob = await qrPattern.getRawData('svg')
	if (!(svgBlob instanceof Blob)) {
		return null
	}
	const image = await loadSvgImage(svgBlob)
	if (!image) {
		return null
	}
	const { width: patternWidth, height: patternHeight } = qrPattern._options
	const canvas = document.createElement('canvas')
	canvas.width = patternWidth + QUIET_ZONE_PX * 2
	canvas.height = patternHeight + QUIET_ZONE_PX * 2
	const context = canvas.getContext('2d', { willReadFrequently: true })
	if (!context) {
		return null
	}
	context.fillStyle = '#FFFFFF'
	context.fillRect(0, 0, canvas.width, canvas.height)
	if (backgroundColor) {
		context.fillStyle = backgroundColor
		context.fillRect(0, 0, canvas.width, canvas.height)
	}
	context.drawImage(
		image,
		QUIET_ZONE_PX,
		QUIET_ZONE_PX,
		patternWidth,
		patternHeight,
	)
	const { data, width, height } = context.getImageData(
		0,
		0,
		canvas.width,
		canvas.height,
	)
	const { default: jsQR } = await import('jsqr')
	return (
		jsQR(data, width, height, { inversionAttempts: 'attemptBoth' })?.data ??
		null
	)
}

const setLogo = (
	qrPattern: QRCodeStyling,
	{ image, imageSize }: { image: string; imageSize: number },
) =>
	qrPattern.update({
		image,
		imageOptions: { ...qrPattern._options.imageOptions, imageSize },
	})

export const getScannableLogoImageSize = async (
	qrDesign: TQrDesign & { data: string },
) => {
	if (!hasLogo(qrDesign)) {
		return null
	}

	const { qrPattern } = await createQrPatternInstance({
		...qrDesign,
		imageOptions: {
			...qrDesign.imageOptions,
			imageSize: MAX_LOGO_IMAGE_SIZE,
		},
	})
	const image = qrPattern._options.image
	const fittedImageSize = qrPattern._options.imageOptions.imageSize
	const backgroundColor = qrPattern._options.backgroundOptions?.color
	const isDecoded = async () =>
		(await decodeQrPattern(qrPattern, backgroundColor)) === qrDesign.data

	if (!image || (await isDecoded())) {
		return fittedImageSize
	}

	setLogo(qrPattern, { image: '', imageSize: fittedImageSize })
	if (!(await isDecoded())) {
		return fittedImageSize
	}

	let imageSize = fittedImageSize
	for (let step = 0; step < MAX_SHRINK_STEPS; step++) {
		imageSize *= SHRINK_FACTOR
		setLogo(qrPattern, { image, imageSize })
		if (await isDecoded()) {
			return imageSize
		}
	}

	return imageSize
}
